"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface CatalogCollapsibleContentProps {
  children: ReactNode;
  contentId: string;
  expanded: boolean;
  forceExpanded?: boolean;
  collapseOnMobile?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  showLessLabel: string;
  showMoreLabel: string;
}

function CatalogCollapsibleToggle({
  collapseOnMobile,
  contentId,
  expanded,
  label,
  onClick,
}: {
  collapseOnMobile: boolean;
  contentId: string;
  expanded: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-controls={contentId}
      aria-expanded={expanded}
      onClick={onClick}
      className={cn(
        "inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        collapseOnMobile && "md:hidden",
      )}
    >
      {label}
    </button>
  );
}

function resolvedExpanded(expanded: boolean, forceExpanded: boolean) {
  return expanded || forceExpanded;
}

export function CatalogCollapsibleContent({
  children,
  contentId,
  expanded,
  forceExpanded = false,
  collapseOnMobile = false,
  onExpandedChange,
  showLessLabel,
  showMoreLabel,
}: CatalogCollapsibleContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isExpanded = resolvedExpanded(expanded, forceExpanded);

  useEffect(() => {
    if (isExpanded) return;

    const content = contentRef.current;
    if (!content) return;

    const updateOverflow = () => {
      setIsOverflowing(content.scrollHeight > content.clientHeight + 1);
    };

    updateOverflow();
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateOverflow);
    resizeObserver?.observe(content);
    window.addEventListener("resize", updateOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [isExpanded]);

  const toggle = (
    <CatalogCollapsibleToggle
      collapseOnMobile={collapseOnMobile}
      contentId={contentId}
      expanded={isExpanded}
      label={isExpanded ? showLessLabel : showMoreLabel}
      onClick={() => onExpandedChange(!expanded)}
    />
  );

  return (
    <div className="relative">
      <div
        ref={contentRef}
        id={contentId}
        className={cn(
          !isExpanded && "max-h-72 overflow-hidden",
          !isExpanded && collapseOnMobile && "md:max-h-none md:overflow-visible",
        )}
      >
        {children}
      </div>

      {isOverflowing &&
        !forceExpanded &&
        (isExpanded ? (
          <div className="mt-4 flex justify-center">{toggle}</div>
        ) : (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-24 items-end justify-center bg-gradient-to-b from-transparent via-background/90 to-background pb-1">
            <div className="pointer-events-auto">{toggle}</div>
          </div>
        ))}
    </div>
  );
}
